import { useEffect, useState } from "react";
import { Loader2, User2, Calendar, AlertTriangle, FileText, Navigation, MapPin, Clock, ChevronRight, CheckCircle2, BadgeCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import { useEmployeeApi } from "../employeeApi";
import { jobDateKey } from "../calendarDates";

const STAGE_PILL = { paa_vej: "På vej", i_gang: "I gang", faerdig: "Færdig", modtaget: "Ny" };

function greeting() { const h = new Date().getHours(); return h < 10 ? "Godmorgen" : h < 18 ? "Goddag" : "Godaften"; }
function today() { const s = new Date().toLocaleDateString("da-DK", { weekday: "long", day: "numeric", month: "long" }); return s.charAt(0).toUpperCase() + s.slice(1); }

function Kpi({ icon: Icon, color, value, label, testId }) {
    return (
        <div data-testid={testId} className="flex-1 min-w-0 bg-white rounded-2xl border border-slate-200 shadow-sm px-2 py-3 flex flex-wrap items-center gap-2">
            <Icon className={`w-6 h-6 shrink-0 ${color}`} />
            <div><p className="text-xl font-bold text-slate-900 leading-none">{value}</p><p className="text-[11px] text-slate-500 mt-0.5">{label}</p></div>
        </div>
    );
}

export default function HomeScreen({ onOpenJob, onGoTasks }) {
    const api = useEmployeeApi();
    const [jobs, setJobs] = useState(null);
    const [me, setMe] = useState(null);
    const [error, setError] = useState(false);
    const [retry, setRetry] = useState(0);

    useEffect(() => {
        setError(false);
        Promise.all([api.getJobs(), api.getProfile()]).then(([data, profile]) => { setJobs(data.jobs || []); setMe(profile); }).catch(() => setError(true));
    }, [api, retry]);

    if (error) return <div role="alert" data-testid="home-error" className="space-y-3 text-sm text-red-700">Kunne ikke hente oversigten.<button onClick={() => setRetry((n) => n + 1)} data-testid="home-retry" className="block text-blue-600">Prøv igen</button></div>;
    if (!jobs || !me) return <div className="flex justify-center py-20" data-testid="home-loading"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

    const isDone = (job) => ["færdig", "afsluttet", "done", "completed", "klar", "invoice_ready", "invoiced"].includes((job.status || "").toLowerCase());
    const done = jobs.filter(isDone);
    const active = jobs.filter((job) => !isDone(job) && !["cancelled", "annulleret"].includes((job.status || "").toLowerCase()));
    // "Din næste opgave": kun udførbare jobs (ikke afsluttet, ikke blokeret af specialbestilling).
    const selectable = active.filter((j) => !j.locked);
    const isAkut = (j) => (j.priority || "").toLowerCase() === "høj";
    const timeVal = (j) => `${jobDateKey(j) || "9999-12-31"} ${(j.time || "99:99").replace(".", ":")}`;
    const oldVal = (j) => String(j.created_at || j.date || j.deadline || j.id || "");
    const tier = (j) => (isAkut(j) ? 0 : j.time ? 1 : 2);
    // Prioritet: Akut først → tidsbestemt (nærmeste kommende tid) → ældste normale job.
    const cmp = (a, b) => {
        const ta = tier(a), tb = tier(b);
        if (ta !== tb) return ta - tb;
        if (ta === 2) return oldVal(a).localeCompare(oldVal(b));
        return timeVal(a).localeCompare(timeVal(b));
    };
    const sortedSelectable = [...selectable].sort(cmp);
    const next = sortedSelectable[0];
    const blocked = active.filter((j) => j.locked);
    const later = [...sortedSelectable.slice(1), ...blocked];
    const firstName = (me.name || "").split(" ")[0] || "";

    return (
        <div className="space-y-5" data-testid="employee-home">
            <div>
                <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Hjem</h1>
                <p data-testid="home-greeting" className="text-lg text-slate-700 mt-1 break-words">{greeting()}, {firstName}</p>
                <p data-testid="home-date" className="text-sm text-slate-400">{today()}</p>
            </div>

            <button onClick={() => toast(`${me.name} · ${me.title || me.role}`)} data-testid="home-profile"
                className="w-full bg-white rounded-2xl border border-slate-200 shadow-sm px-4 py-3.5 flex items-center gap-3 active:scale-[0.99] transition-transform">
                <User2 className="w-6 h-6 text-slate-500 shrink-0" />
                <div className="flex-1 min-w-0 text-left">
                    <p data-testid="home-profile-name" className="font-bold text-slate-900 break-words">{me.name} · <span className="font-semibold">{me.title || me.role}</span> <BadgeCheck className="w-4 h-4 text-blue-500 inline" /></p>
                    <p className="text-sm text-slate-500">Logget ind</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-300" />
            </button>

            <div className="flex gap-2.5" data-testid="home-kpis">
                <Kpi testId="home-total-count" icon={Calendar} color="text-blue-600" value={jobs.length} label="opgaver" />
                <Kpi testId="home-active-count" icon={AlertTriangle} color="text-red-500" value={active.length} label="tilbage" />
                <Kpi testId="home-done-count" icon={FileText} color="text-emerald-600" value={done.length} label="afsluttede" />
            </div>

            <div>
                <h2 className="text-xl font-bold text-slate-900 mb-2">Din næste opgave</h2>
                {next ? (
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4" data-testid="home-next-job">
                        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 bg-blue-50 px-3 py-1 rounded-full"><Navigation className="w-4 h-4" />{STAGE_PILL[next.stage] || "Ny"}</span>
                        <div className="mt-3 space-y-2">
                            <p className="text-lg font-bold text-slate-900 flex items-center gap-2"><FileText className="w-5 h-5 text-slate-400" />{next.task}</p>
                            <p className="text-slate-700 flex items-center gap-2"><User2 className="w-4 h-4 text-slate-400" />{next.customer || "Ukendt kunde"}</p>
                            {next.address && <p className="text-slate-700 flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400" />{next.address}</p>}
                            {next.time && <p className="text-slate-700 flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400" />Ankomst ca. {next.time}</p>}
                        </div>
                        <button onClick={() => onOpenJob(next.id)} data-testid="home-open-next" className="mt-3 w-full h-12 rounded-xl bg-blue-600 text-white font-bold flex items-center justify-center gap-2 active:scale-[0.99] transition-transform">Åbn opgaven <ChevronRight className="w-4 h-4" /></button>
                    </div>
                ) : <div className="rounded-2xl bg-white border border-slate-200 p-5 text-slate-500 text-sm flex items-center gap-2 shadow-sm"><CheckCircle2 className="w-5 h-5 text-emerald-500" />Ingen aktive opgaver.</div>}
            </div>

            <div>
                <h2 className="text-lg font-bold text-slate-900 mb-2">Øvrige opgaver</h2>
                <div className="space-y-3">
                    {later.map((j) => (
                        <button key={j.id} onClick={() => onOpenJob(j.id)} data-testid={`home-later-${j.id}`} className="w-full bg-white rounded-2xl border border-slate-200 shadow-sm px-4 py-3.5 flex items-center gap-3 active:scale-[0.99] transition-transform">
                            {j.locked ? <Lock className="w-6 h-6 text-amber-500 shrink-0" /> : <Calendar className="w-6 h-6 text-slate-400 shrink-0" />}
                            <span className="font-bold text-slate-900 w-14 shrink-0 text-xs">{j.locked ? "🔒" : (j.time || "")}</span>
                            <div className="flex-1 text-left min-w-0"><p className="font-semibold text-slate-900 truncate">{j.task}</p><p className="text-sm text-slate-500 truncate">{j.locked ? "Afventer vare" : (j.address || j.customer)}</p></div>
                            <ChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
                        </button>
                    ))}
                    <button onClick={() => { sessionStorage.setItem("msh_emp_tasks_filter", "afsluttede"); onGoTasks && onGoTasks(); }} data-testid="home-fakturaklar" className="w-full bg-white rounded-2xl border border-slate-200 shadow-sm px-4 py-3.5 flex items-center gap-3 active:scale-[0.99] transition-transform">
                        <FileText className="w-6 h-6 text-emerald-600 shrink-0" />
                        <div className="flex-1 text-left"><p className="font-bold text-slate-900">Afsluttede opgaver</p></div>
                        <span data-testid="home-completed-count" className="w-7 h-7 rounded-full bg-slate-100 text-slate-600 text-sm font-bold flex items-center justify-center">{done.length}</span>
                        <ChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
                    </button>
                </div>
            </div>
        </div>
    );
}
